/**
 * UTM attribution across pages.
 *
 * A campaign link lands on whatever page it points at; the form is usually on
 * another one, so `window.location.search` is empty by the time anything is
 * submitted. `src/proxy.ts` stashes the params in a cookie on arrival and
 * `useHubspotForm` merges cookie + current URL at submit time, URL winning per
 * key so a fresh campaign link overrides the stash.
 *
 * Not read on the server: `cookies()` in a layout would opt every route out of
 * ISR (see the caching invariants in `AGENTS.md`).
 */

export const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export const UTM_COOKIE = "utm_params";
/** 30 days — a campaign outlives a session, but attribution stays last-touch. */
export const UTM_TTL_SECONDS = 60 * 60 * 24 * 30;

/** The utm_* entries of a query string. Empty object when there are none. */
export const pickUtmParams = (
  search: URLSearchParams
): Record<string, string> => {
  const utm: Record<string, string> = {};
  for (const key of UTM_PARAMS) {
    const value = search.get(key);
    if (value) utm[key] = value;
  }
  return utm;
};

/** Cookie payload: a query string, so there is no JSON escaping to get wrong. */
export const serializeUtm = (utm: Record<string, string>): string =>
  new URLSearchParams(utm).toString();

/**
 * Reads the stash out of a raw `document.cookie` string. Kept pure and
 * testable. The value arrives percent-encoded (the middleware's cookie
 * writer escapes it) and `document.cookie` hands back the
 * stored octets verbatim, so it has to be decoded before parsing. Anything
 * unparseable is treated as no stash — a hand-edited cookie must not throw in
 * the middle of a submission.
 */
export const readUtmCookie = (cookie: string): Record<string, string> => {
  const prefix = `${UTM_COOKIE}=`;
  const entry = cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  if (!entry) return {};
  try {
    return pickUtmParams(
      new URLSearchParams(decodeURIComponent(entry.slice(prefix.length)))
    );
  } catch {
    return {};
  }
};
